<template>
  <div>
    <div v-if="businessData.id">
      <a href="https://uk.trustpilot.com/review/thethinkingtraveller.com" target="_blank" :title="`TrustPilot ${businessData.score.trustScore}/5 stars rating`">
        <div class="trustpilot" :style="layout">
          <div class="trustpilot__rating">{{ businessData.rating }}</div>
          <div class="trustpilot__stars">
            <img :src="`https://images-static.trustpilot.com/api/stars/${businessData.score.stars}/star.svg`" :alt="`${businessData.score.trustScore} stars rating`" class="trustpilot__stars--stars lazyload">
            <div class="trustpilot__stars--text">{{ businessData.score.trustScore }}/5</div>
          </div>
          <div class="trustpilot__reviews">
            <div class="trustpilot__reviews--text">{{ businessData.numberOfReviews.total }} reviews on</div>
            <img :src="darkText ? 'https://images-static.trustpilot.com/api/logos/light-bg/logo.svg' : 'https://images-static.trustpilot.com/api/logos/dark-bg/logo.svg'" alt="TrustPilot logo" class="trustpilot__reviews--logo lazyload">
          </div>
        </div>
      </a>
    </div>

    <div v-if="showReviews && reviews.length > 0" class="reviews-container">
      <div v-for="(review,i) in reviews" :key="i" class="review-block">
        <div v-if="review.text.length > 1" class="review-block__review" aria-label="Trust Pilot Customer Review">
          <div class="review-block__header">
            <div class="review-block__header--name" aria-label="Reviewer name">
              {{ review.consumer.displayName || review.consumer.name }}
            </div>
            <div class="review-block__header--date" aria-label="Review creation date">
              {{ formatDate(review.createdAt) }}
            </div>
          </div>
          <div class="review-block__stars" aria-label="Review Star Rating">
            <img :data-src="getStarImage(review.stars)" :alt="`${review.stars} stars rating`" class="review-block__stars--star lazyload">
          </div>
          <a href="https://uk.trustpilot.com/review/thethinkingtraveller.com" class="text-dark">
            <div class="review-block__content--title" aria-label="Review title">
              {{ review.title }}
            </div>
            <div class="review-block__content--text" aria-label="Review content">
              {{ review.text }}
            </div>
          </a>
          <div class="review-block__reply" aria-label="Reply from The Thinking Traveller" @click="review.showReply = !review.showReply">
            <div class="review-block__reply--header">
              <div class="review-block__reply--title" aria-label="Reply from The Thinking Traveller">
                <font-awesome-icon :icon="['fas', 'reply']" aria-label="reply" class="review-block__reply--icon" />
                Reply from The Thinking Traveller
              </div>
              <div v-if="review.showReply" class="review-block__reply--date" aria-label="Reply creation date">
                {{ formatDate(review.companyReply.createdAt) }}
              </div>
            </div>
            <div v-if="review.showReply" class="review-block__reply--text" aria-label="Review company reply" v-html="review.companyReply.text.replaceAll('\n', '<br/>')" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TrustPilotReviews',
  props: {
    flexDirection: { type: String, default: 'row' },
    showReviews: { type: Boolean, default: false },
    darkText: { type: Boolean, default: true },
  },
  data() {
    return {
      showReply: false,
    };
  },
  computed: {
    businessData() {
      return this.$store.getters['trustpilot/getBusinessData'];
    },
    reviews() {
      return this.$store.getters['trustpilot/getReviews'];
    },
    layout() {
      return {
        flexDirection: this.flexDirection,
      };
    },
  },
  created() {
    this.getTrustPilotData();
  },
  methods: {
    async getTrustPilotData() {
      await this.$store.dispatch('trustpilot/fetchBusinessData');
      if (this.showReviews) {
        await this.$store.dispatch('trustpilot/fetchAllCompanyReviews');
      }
    },
    getStarImage(stars) {
      return `https://images-static.trustpilot.com/api/stars/${stars}/star.svg`;
    },
    formatDate(date) {
      return moment(date).format('D MMMM YYYY');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/styles/main.scss";
$breakpoint: 490px;

.trustpilot{
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  margin: 20px auto 0 auto;
  max-width: 440px;
  font-family: $font-family-grotesk;
  color: $darkBlack;
  font-size: 12px;
  line-height: 1;
  text-transform: uppercase;
  @media (max-width: $breakpoint) {
    flex-direction: column !important;
    justify-content: space-around;
  }

  &__rating{
    display: flex;
    flex-direction: row;
    align-items: center;
    line-height: 1;
  }

  &__stars{
    display: flex;
    flex-direction: row;
    align-items: center;
    @media (max-width: $breakpoint) {
      margin: 10px 0;
    }

    &--stars{
      width: 110px;
      height: auto;
      margin-right: 5px;
    }
  }

   &__reviews{
    display: flex;
    flex-direction: row;
    align-items: center;

    &--text{
      margin:0 2px 0 0;
    }

    &--logo{
      width:80px;
      height: auto;
      margin:0 0 5px 0;
    }
  }
}

.reviews-container{
  max-height: 860px;
  max-width: 900px;
  overflow-y: scroll;
  margin: 30px auto 20px auto;
  padding: 0 50px;
  @media (max-width:580px) {
    padding: 0 15px 0 0;
    max-height: 1000px;
  }
}

.review-block{
  font-family: $font-family-editorial;
  color: $airforce-blue;
  font-size: 16px;
  border-bottom: 1px solid $solitude;
  padding: 0 0 10px 0;
  margin: 0 0 20px 0;

  &__header{
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 5px 0;

    &--name {
      font-weight: 500;
    }
    &--date {
      font-family: $font-family-grotesk;
      font-size: 16px;
    }
  }

  &__stars{
    margin: 0 0 5px 0;

    &--star{
      width:135px;
      height: auto;
    }

  }

  &__content{
    font-size: 15px;
    &--title{
      font-weight: 500;
    }
    &--text{
      font-family: $font-family-grotesk;
    }
  }

  &__reply{
    margin: 20px 0;
    font-family: $font-family-grotesk;
    background-color: $offwhite;
    border-left: 3px solid rgb(0, 182, 122);;
    padding:15px 20px;
    cursor: pointer;
    @media (max-width:360px) {
      padding:15px 10px;
    }

    &--header{
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
       @media (max-width:480px) {
        flex-direction: column;
       }
    }

    &--title{
      font-style: italic;
    }

    &--icon{
      color: rgb(0, 182, 122);
      margin: 0 10px 0 0;
    }

    &--date {
      font-family: $font-family-grotesk;
      font-size: 15px;
    }

    &--text{
      margin: 10px 30px 0 30px;
      @media (max-width:350px) {
        margin: 10px 10px 0 10px;
      }
    }
  }
}

</style>
